import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../../utils/api';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentIcon,
  EyeIcon
} from '../Icons';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  useEffect(() => {
    fetchClients();
  }, [searchTerm, pagination.page]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort: 'lastName,firstName',
        ...(searchTerm && { search: searchTerm })
      };

      const response = await endpoints.clients.list(params);
      setClients(Array.isArray(response.clients) ? response.clients : []);
      setPagination(prev => ({ 
        ...prev, 
        total: response.pagination?.total || 0,
        pages: Math.max(0, response.pagination?.pages || 0),
        page: Math.max(1, response.pagination?.page || prev.page),
        limit: response.pagination?.limit || prev.limit
      }));
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setPagination({ page: 1, limit: 20, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your client database and track their cases.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Link
            to="/app/clients/new"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            New Client
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search clients by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading clients...</p>
          </div>
        ) : clients.length === 0 ? (
          <div className="p-12 text-center">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No clients found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? 'Try adjusting your search criteria.'
                : 'Get started by adding your first client.'
              }
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <Link
                  to="/app/clients/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  New Client
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {clients.map((client) => (
                <div key={client.id} className="flex flex-col gap-3 px-4 py-4 hover:bg-gray-50 sm:flex-row sm:items-center sm:gap-5 sm:px-6">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="h-10 w-10 flex-shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-blue-600">
                        {client.lastName?.charAt(0) || 'L'}{client.firstName?.charAt(0) || 'C'}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {client.lastName || 'Client'}, {client.firstName || 'Unknown'}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Added {new Date(client.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="grid min-w-0 flex-1 grid-cols-1 gap-1 text-xs text-gray-600 sm:grid-cols-2 sm:gap-x-6">
                    <div className="flex min-w-0 items-center">
                      <EnvelopeIcon className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span className="truncate">{client.email || 'No email'}</span>
                    </div>
                    <div className="flex items-center">
                      <PhoneIcon className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span>{client.phone || 'No phone'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-4 sm:w-56 sm:justify-end">
                    <div className="flex items-center text-xs text-gray-500">
                      <DocumentIcon className="mr-2 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <span>{client._count?.cases || 0} cases</span>
                    </div>
                    <Link
                      to={`/app/clients/${client.id}`}
                      className="inline-flex flex-shrink-0 items-center text-sm font-medium text-blue-600 hover:text-blue-500"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && pagination.pages && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {(pagination.page - 1) * pagination.limit + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{pagination.total}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      {Array.from({ length: Math.max(0, pagination.pages || 0) }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setPagination(prev => ({ ...prev, page }))}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pagination.page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } ${page === 1 ? 'rounded-l-md' : ''} ${page === pagination.pages ? 'rounded-r-md' : ''}`}
                        >
                          {page}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientList;
